import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Handles the /#session_id=... fragment that Emergent Auth redirects back to.
// Exchanges it for a long-lived cookie, then sends the user to /garden.
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = React.useState('');
  const processed = React.useRef(false);

  React.useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const match = (window.location.hash || '').match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';
    if (!sessionId) {
      setError('Missing session id');
      setTimeout(() => navigate('/', { replace: true }), 1500);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) throw new Error('exchange failed');
        const data = await res.json();
        setUser(data.user);
        // Clean the hash then route to garden
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/garden', { replace: true, state: { user: data.user } });
      } catch (e) {
        setError('Could not complete sign-in. Please try again.');
        setTimeout(() => navigate('/', { replace: true }), 1800);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="auth-callback">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-brand-gold text-xs tracking-[0.25em] uppercase mb-5">
          <Leaf className="w-3.5 h-3.5" /> Pallavit
        </div>
        {error ? (
          <div className="text-red-700 font-serif text-lg">{error}</div>
        ) : (
          <div className="flex items-center gap-3 text-brand-ink2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-serif italic">Planting your session…</span>
          </div>
        )}
      </div>
    </div>
  );
}
